if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerTop = this.header.querySelector('.header__top');
      this.megaMenuWrapper = document.querySelector('.mega-menu__wrapper');
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
      this.stickyDisabledOnMobile = this.hasAttribute('data-sticky-disable-mobile');
      this.headerBounds = {};
      this.safeTopArea = 300;

      this.safeHeight = window.innerHeight * 0.75

      this.setHeaderHeight();

      this.resizeHandler = () => {
        clearTimeout(this.resizeDebounceTimer);
        this.resizeDebounceTimer = setTimeout(() => {
          this.setHeaderHeight();
        }, 100);
      };

      window.addEventListener('resize', this.resizeHandler);

      if (this.headerIsAlwaysSticky && !this.isStickyDisabled()) {
        this.header.classList.add('shopify-section-header-sticky');
        document.body.classList.add('has-header-sticky');
      }

      this.isOpen = true;
      this.isHidden = false;
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.ignoreFirstScroll = true;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => {
        this.preventReveal = true;
      };

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      this.revealHeaderOnScroll = () => {
        this.preventReveal = false;
      };
      this.addEventListener('allowHeaderReveal', this.revealHeaderOnScroll);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true, capture: false });

      window.addEventListener('load', () => {
        setTimeout(() => {
          this.ignoreFirstScroll = false;
        }, 100);
      });

      this.createObserver();

      this.header.classList.add('is-initialized');
    }

    updateBodyVisibleClass() {
      const isHidden = this.header.classList.contains('shopify-section-header-hidden');
      const isVisible = this.header.classList.contains('scrolled-past-header') && !isHidden;

      document.body.classList.toggle('has-section-header-visible', isVisible);
      document.body.classList.toggle('has-section-header-hidden', isHidden);
    }

    isStickyDisabled() {
      return this.stickyDisabledOnMobile && DeviceUtils.isMobile;
    }

    setHeaderHeight() {
      const headerTopHeight = this.headerTop ? this.headerTop.offsetHeight : 0;
      const headerHeight = this.header.offsetHeight - headerTopHeight;
      this.headerTopHeight = headerTopHeight;
      this.headerBounds = this.header.getBoundingClientRect();

      document.documentElement.style.setProperty('--sticky-header-height', `-${headerHeight}px`);
      document.documentElement.style.setProperty('--sticky-header-top-height', `-${headerTopHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      this.removeEventListener('allowHeaderReveal', this.revealHeaderOnScroll);
      window.removeEventListener('scroll', this.onScrollHandler, { passive: true, capture: false });
      window.removeEventListener('resize', this.resizeHandler);
      clearTimeout(this.resizeDebounceTimer);
    }

    createObserver() {
      const observer = new IntersectionObserver((entries) => {
        this.headerBounds = entries[0].intersectionRect;

        observer.disconnect();

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.currentScrollTop = scrollTop;

        if (scrollTop > this.headerBounds.bottom) {
          if (!this.isStickyDisabled()) {
            this.reveal();
          }
          this.header.classList.add('scrolled-past-header');
          this.updateShadowClass(scrollTop);
        } else {
          this.checkStartScrollPosition();
        }
        this.updateBodyVisibleClass();
      });
      observer.observe(this.header);
    }

    checkStartScrollPosition() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.currentScrollTop = scrollTop;
      this.updateShadowClass(scrollTop);
      this.updateBodyVisibleClass();
    }

    updateShadowClass(scrollTop) {
      const hasShadow = scrollTop > this.headerTopHeight;
      if (hasShadow) {
        this.header.classList.add('header-with-shadow');
      } else {
        this.header.classList.remove('header-with-shadow');
      }
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const headerBottom = this.headerBounds.bottom;

      this.updateShadowClass(scrollTop);

      if (scrollTop <= headerBottom) {
        if (document.body.classList.contains('block-sticky')) {
          this.currentScrollTop = scrollTop;
          return;
        }

        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(() => {
          this.reset();
        });

        this.currentScrollTop = scrollTop;
        this.updateBodyVisibleClass();
        return;
      }

      if (this.ignoreFirstScroll || scrollTop < this.safeHeight || document.body.hasAttribute('data-mega-menu-block') || this.isStickyDisabled() || this.predictiveSearch?.isOpen) {
        this.currentScrollTop = scrollTop;
        return;
      }

      const scrollDelta = scrollTop - this.currentScrollTop;
      const isScrollingDown = scrollDelta > 0;
      const isScrollingUp = scrollDelta < 0;

      if (isScrollingDown && scrollTop > headerBottom + this.safeTopArea) {
        this.header.classList.add('scrolled-past-header');

        if (this.preventHide) {
          this.currentScrollTop = scrollTop;
          return;
        }

        requestAnimationFrame(() => {
          this.hide();
        });
      } else if (isScrollingUp && scrollTop > headerBottom) {
        this.header.classList.add('scrolled-past-header');

        if (!this.preventReveal) {
          requestAnimationFrame(() => {
            this.reveal();
          });
        } else {
          clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 65);
          requestAnimationFrame(() => {
            this.hide();
          });
        }
      }

      this.currentScrollTop = scrollTop;
      this.updateBodyVisibleClass();
    }

    hide() {
      if (this.isHidden ||
        this.headerIsAlwaysSticky ||
        this.megaMenuWrapper?.classList.contains('open') ||
        document.querySelector('.mega-menu--block') ||
        document.body.classList.contains('has-account-open')) {
        return;
      }

      if (!this.isHidden) {
        this.closeMenuDisclosure();
        this.closeModal();
        this.isHidden = true;
      }

      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.updateBodyVisibleClass();
    }

    reveal() {
      if (!this.isHidden || this.headerIsAlwaysSticky || this.isStickyDisabled()) {
        return;
      }
      this.header.classList.add('animate');
      requestAnimationFrame(() => {
        this.header.classList.add('shopify-section-header-sticky');
        this.header.classList.remove('shopify-section-header-hidden');
      });

      this.isHidden = false;
      this.updateBodyVisibleClass();
    }

    reset() {

      if (!this.isHidden && !this.isVisible) return;

      if (this.headerIsAlwaysSticky || this.isStickyDisabled()) {
        return;
      }

      this.header.classList.remove('shopify-section-header-hidden');
      requestAnimationFrame(() => {
        this.header.classList.remove('animate', 'shopify-section-header-sticky');
      });

      this.isHidden = false;
      this.updateBodyVisibleClass();
    }

    closeMenuDisclosure() {
      document.querySelector('mega-menu')?.forceClose?.();
    }

    closeModal() {
      document.querySelectorAll('predictive-search').forEach(element => {
        if (!element.searchTemplate) {
          element.forceClose?.();
        }
      });

      document.querySelectorAll('localization-form, details-modal').forEach(element => {
        element.forceClose?.();
      });
    }

    get isVisible() {
      return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden');
    }

    get isHiddenState() {
      return this.header.classList.contains('shopify-section-header-hidden');
    }
  });
}

/* Keeps the topline side columns equal to the wider one, so the announcement stays centered */
if (!customElements.get('header-topline')) {
  customElements.define('header-topline', class HeaderTopline extends HTMLElement {
    connectedCallback() {
      this.sides = [
        this.querySelector('.header__localization .desktop-localization-wrapper'),
        this.querySelector('.header__social-links .list-social')
      ].filter(Boolean);

      if (!this.sides.length) return;

      this.updateColumns = this.updateColumns.bind(this);
      this.updateColumns();
      document.fonts?.ready.then(this.updateColumns);

      this.desktopQuery = window.matchMedia('(min-width: 1025px)');
      this.desktopQuery.addEventListener('change', this.updateColumns);
    }

    disconnectedCallback() {
      this.desktopQuery?.removeEventListener('change', this.updateColumns);
    }

    updateColumns() {
      if (DeviceUtils.isMobile) return;

      const width = this.sides.reduce((max, side) => Math.max(max, Math.ceil(side.getBoundingClientRect().width)), 0);

      if (this.lastWidth === width) return;
      this.lastWidth = width;

      if (width > 0) {
        this.style.setProperty('--topline-columns', `${width}px minmax(0, 1fr) ${width}px`);
      }
    }
  });
}
